import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "onBlur", "onClick", "onFocus", "onKeyDown"];
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useContext } from 'react';
import { AccordionItemContext } from '../AccordionItem/AccordionItem.context';
import { Flex } from '../Flex/Flex';
import IconChevronDown from '../Icon/icons/IconChevronDown';
import { StyledAccordionHeader } from './AccordionHeader.styles';

/**
 * Interactive heading text that hides or reveals the content in its sibling
 * `Accordion.Panel`.
 *
 * @since 8.43.0
 */

export var AccordionHeader = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var children = _ref.children,
    onBlur = _ref.onBlur,
    onClick = _ref.onClick,
    onFocus = _ref.onFocus,
    onKeyDown = _ref.onKeyDown,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useContext = useContext(AccordionItemContext),
    expanded = _useContext.expanded,
    headerId = _useContext.headerId,
    panelId = _useContext.panelId,
    ref = _useContext.ref,
    onBlurContext = _useContext.onBlur,
    onClickContext = _useContext.onClick,
    onFocusContext = _useContext.onFocus,
    onKeyDownContext = _useContext.onKeyDown;
  var innerRef = useCallback(function (element) {
    if (forwardedRef) {
      if (typeof forwardedRef === 'function') {
        forwardedRef(element);
      } else {
        // eslint-disable-next-line no-param-reassign
        forwardedRef.current = element;
      }
    }
    if (ref) {
      ref(element);
    }
  }, [ref, forwardedRef]);
  var onBlurHandler = useCallback(function (event) {
    onBlurContext(event);
    if (typeof onBlur === 'function') {
      onBlur(event);
    }
  }, [onBlur, onBlurContext]);
  var onClickHandler = useCallback(function (event) {
    onClickContext(event);
    if (typeof onClick === 'function') {
      onClick(event);
    }
  }, [onClick, onClickContext]);
  var onFocusHandler = useCallback(function (event) {
    onFocusContext(event);
    if (typeof onFocus === 'function') {
      onFocus(event);
    }
  }, [onFocus, onFocusContext]);
  var onKeyDownHandler = useCallback(function (event) {
    onKeyDownContext(event);
    if (typeof onKeyDown === 'function') {
      onKeyDown(event);
    }
  }, [onKeyDown, onKeyDownContext]);
  return /*#__PURE__*/React.createElement(StyledAccordionHeader, _extends({
    ref: innerRef,
    "aria-controls": panelId,
    "aria-expanded": expanded,
    id: headerId,
    tabIndex: 0,
    onBlur: onBlurHandler,
    onClick: onClickHandler,
    onFocus: onFocusHandler,
    onKeyDown: onKeyDownHandler
  }, rest), /*#__PURE__*/React.createElement(IconChevronDown, {
    size: "xs",
    marginRight: "sm"
  }), /*#__PURE__*/React.createElement(Flex, {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexGrow: 1
  }, children));
});
AccordionHeader.displayName = 'Accordion.Header';
AccordionHeader.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The styled-components
   * [polymorphic "as"](https://styled-components.com/docs/api#as-polymorphic-prop) prop.
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  as: PropTypes.any,
  children: PropTypes.node.isRequired,
  /**
   * @ignore
   */
  onBlur: PropTypes.func,
  /**
   * @ignore
   */
  onClick: PropTypes.func,
  /**
   * @ignore
   */
  onFocus: PropTypes.func,
  /**
   * @ignore
   */
  onKeyDown: PropTypes.func,
  /**
   * A [role](https://www.w3.org/TR/wai-aria-1.2/#roles) is required for assistive
   * technologies to announce the header properly. Header uses the
   * [button](https://www.w3.org/TR/wai-aria-1.2/#region) role.
   */
  role: PropTypes.string
} : {};
AccordionHeader.defaultProps = {
  role: 'button'
};